/**
 * SVGFrameAnimator - A reusable class for animating sequences of SVG files
 *
 * Usage Example:
 *
 * const animator = new SVGFrameAnimator('my-container-id', {
 *     frames: [
 *         'path/to/frame1.svg',
 *         'path/to/frame2.svg',
 *         'path/to/frame3.svg'
 *     ],
 *     frameRate: 24,
 *     loop: true,
 *     autoplay: false
 * });
 *
 * animator.play();
 *
 * OR with a pattern-based approach:
 *
 * const animator = new SVGFrameAnimator('my-container-id', {
 *     basePath: 'assets/animation/frame-',
 *     frameCount: 12,
 *     frameFormat: 'sequential', // 'sequential' or 'padded'
 *     extension: '.svg',
 *     frameRate: 12,
 *     loop: true,
 *     autoplay: true
 * });
 */

export class SVGFrameAnimator {
    constructor(containerId, options = {}) {
        // Find the container element
        this.container = typeof containerId === 'string'
            ? document.getElementById(containerId)
            : containerId;

        if (!this.container) {
            console.error(`Container with id "${containerId}" not found`);
            return;
        }

        // Set default options
        this.options = {
            frames: options.frames || null,
            basePath: options.basePath || '',
            frameCount: options.frameCount || 0,
            frameFormat: options.frameFormat || 'sequential', // 'sequential' | 'padded'
            paddingLength: options.paddingLength || 2, // For padded format (e.g., 01, 02, 03)
            extension: options.extension || '.svg',
            frameRate: options.frameRate || 24,
            loop: options.loop !== undefined ? options.loop : true,
            autoplay: options.autoplay !== undefined ? options.autoplay : false,
            imageStyles: options.imageStyles || {
                width: '100%',
                height: '100%',
                objectFit: 'contain'
            },
            onComplete: options.onComplete || null,
            onFrameChange: options.onFrameChange || null,
            preload: options.preload !== undefined ? options.preload : true
        };

        // Animation state
        this.currentFrame = 0;
        this.isPlaying = false;
        this.animationTimer = null;
        this.imageElement = null;
        this.frameList = [];

        // Initialize
        this.init();
    }

    /**
     * Initialize the animator
     */
    init() {
        // Generate frame list
        this.generateFrameList();

        // Create the image element
        this.createImageElement();

        // Preload images if enabled
        if (this.options.preload) {
            this.preloadFrames();
        }

        // Set initial frame
        this.goToFrame(0);

        // Autoplay if enabled
        if (this.options.autoplay) {
            this.play();
        }
    }

    /**
     * Generate the list of frame URLs
     */
    generateFrameList() {
        // If frames are explicitly provided, use them
        if (this.options.frames && this.options.frames.length > 0) {
            this.frameList = this.options.frames;
            return;
        }

        // Otherwise, generate frames from basePath and frameCount
        if (this.options.basePath && this.options.frameCount > 0) {
            this.frameList = [];

            for (let i = 1; i <= this.options.frameCount; i++) {
                let frameNumber;

                if (this.options.frameFormat === 'padded') {
                    frameNumber = String(i).padStart(this.options.paddingLength, '0');
                } else {
                    frameNumber = String(i);
                }

                const frameUrl = `${this.options.basePath}${frameNumber}${this.options.extension}`;
                this.frameList.push(frameUrl);
            }
        }

        if (this.frameList.length === 0) {
            console.error('No frames provided. Please specify either "frames" array or "basePath" with "frameCount"');
        }
    }

    /**
     * Create the image element for displaying frames
     */
    createImageElement() {
        this.imageElement = document.createElement('img');

        // Apply custom styles
        Object.entries(this.options.imageStyles).forEach(([property, value]) => {
            this.imageElement.style[property] = value;
        });

        this.imageElement.alt = 'SVG Frame Animation';

        // Add to container
        this.container.appendChild(this.imageElement);
    }

    /**
     * Preload all frames for smoother animation
     */
    preloadFrames() {
        this.frameList.forEach(framePath => {
            const img = new Image();
            img.src = framePath;
        });
    }

    /**
     * Update the displayed frame
     */
    updateFrame(frameIndex) {
        if (frameIndex >= 0 && frameIndex < this.frameList.length && this.imageElement) {
            this.imageElement.src = this.frameList[frameIndex];

            // Fire frame change callback
            if (this.options.onFrameChange) {
                this.options.onFrameChange(frameIndex, this.frameList[frameIndex]);
            }
        }
    }

    /**
     * Start playing the animation
     */
    play() {
        if (this.isPlaying) return;

        this.isPlaying = true;
        const frameDelay = 1000 / this.options.frameRate; // milliseconds per frame

        this.animationTimer = setInterval(() => {
            this.currentFrame++;

            if (this.currentFrame >= this.frameList.length) {
                if (this.options.loop) {
                    this.currentFrame = 0;
                } else {
                    this.stop();

                    // Fire completion callback
                    if (this.options.onComplete) {
                        this.options.onComplete();
                    }
                    return;
                }
            }

            this.updateFrame(this.currentFrame);
        }, frameDelay);
    }

    /**
     * Stop the animation
     */
    stop() {
        if (this.animationTimer) {
            clearInterval(this.animationTimer);
            this.animationTimer = null;
        }
        this.isPlaying = false;
    }

    /**
     * Pause the animation (alias for stop)
     */
    pause() {
        this.stop();
    }

    /**
     * Reset animation to the first frame
     */
    reset() {
        this.stop();
        this.currentFrame = 0;
        this.updateFrame(0);
    }

    /**
     * Set the frame rate and restart if playing
     */
    setFrameRate(fps) {
        const wasPlaying = this.isPlaying;
        this.stop();
        this.options.frameRate = fps;
        if (wasPlaying) {
            this.play();
        }
    }

    /**
     * Jump to a specific frame (0-indexed)
     */
    goToFrame(frameIndex) {
        if (frameIndex >= 0 && frameIndex < this.frameList.length) {
            this.currentFrame = frameIndex;
            this.updateFrame(frameIndex);
        }
    }

    /**
     * Get the current frame index
     */
    getCurrentFrame() {
        return this.currentFrame;
    }

    /**
     * Get total number of frames
     */
    getTotalFrames() {
        return this.frameList.length;
    }

    /**
     * Check if animation is currently playing
     */
    isAnimationPlaying() {
        return this.isPlaying;
    }

    /**
     * Toggle between play and pause
     */
    togglePlayPause() {
        if (this.isPlaying) {
            this.pause();
        } else {
            this.play();
        }
    }

    /**
     * Destroy the animator and clean up
     */
    destroy() {
        this.stop();
        if (this.imageElement && this.imageElement.parentNode) {
            this.imageElement.parentNode.removeChild(this.imageElement);
        }
        this.imageElement = null;
        this.frameList = [];
    }
}

// Export as default as well
export default SVGFrameAnimator;
